"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { useWaves } from "@/hooks/useWaves";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import CommonIntersectionElement from "@/components/utils/CommonIntersectionElement";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { WaveCreatorPreviewItem } from "./WaveCreatorPreviewItem";
import { shortenAddress } from "@/helpers/address.helpers";
interface WaveCreatorPreviewModalContentProps {
  readonly user: ApiProfileMin;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly isApp?: boolean | undefined;
}
export const WaveCreatorPreviewModalContent: React.FC<WaveCreatorPreviewModalContentProps> = t0 => {
  const $ = _c(40);
  const {
    user,
    isOpen,
    onClose,
    isApp: t1
  } = t0;
  const isApp = t1 === undefined ? false : t1;
  const identity = user.handle ?? user.primary_address;
  let t2;
  if ($[0] !== user.handle || $[1] !== user.primary_address) {
    t2 = user.handle ?? shortenAddress(user.primary_address);
    $[0] = user.handle;
    $[1] = user.primary_address;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const displayName = t2;
  let t3;
  if ($[3] !== identity || $[4] !== isOpen) {
    t3 = {
      identity,
      waveName: null,
      enabled: isOpen,
      directMessage: false
    };
    $[3] = identity;
    $[4] = isOpen;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    waves,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    status,
    error
  } = useWaves(t3);
  let t4;
  if ($[6] !== fetchNextPage || $[7] !== hasNextPage || $[8] !== isFetching || $[9] !== isFetchingNextPage || $[10] !== status) {
    t4 = state => {
      if (!state || status === "pending" || isFetching || isFetchingNextPage || !hasNextPage) {
        return;
      }
      fetchNextPage();
    };
    $[6] = fetchNextPage;
    $[7] = hasNextPage;
    $[8] = isFetching;
    $[9] = isFetchingNextPage;
    $[10] = status;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const onBottomIntersection = t4;
  const isInitialLoading = status === "pending" && waves.length === 0;
  const showEmptyState = !isInitialLoading && !isFetching && waves.length === 0 && !error;
  const wavePlural = waves.length === 1 ? "" : "s";
  const waveCountLabel = isInitialLoading ? "Loading waves..." : `Showing ${waves.length} wave${wavePlural}`;
  const t5 = `tw-relative tw-z-[100] tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between ${isApp ? "tw-px-6 tw-py-4" : "tw-p-6"} tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800/60`;
  let t6;
  if ($[12] !== displayName) {
    t6 = <div className="tw-text-xl tw-font-semibold tw-tracking-tight tw-text-iron-100 sm:tw-text-2xl">Waves by {displayName}</div>;
    $[12] = displayName;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== waveCountLabel) {
    t7 = <div className="tw-mt-1 tw-text-sm tw-text-iron-500">{waveCountLabel}</div>;
    $[14] = waveCountLabel;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = <div>{t6}{t7}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== isApp || $[20] !== onClose) {
    t9 = !isApp && <button onClick={onClose} className="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-iron-800/70 tw-bg-iron-950 tw-text-white tw-transition tw-duration-200 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-iron-600/70 active:tw-scale-95 desktop-hover:hover:tw-border-iron-700 desktop-hover:hover:tw-bg-iron-900" aria-label="Close wave list"><XMarkIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" /></button>;
    $[19] = isApp;
    $[20] = onClose;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t5 || $[23] !== t8 || $[24] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[22] = t5;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = `tw-flex-1 ${isApp ? "" : "tw-max-h-[calc(75vh-120px)] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-max-h-[calc(90vh-140px)]"}`;
  let t12;
  if ($[26] !== error || $[27] !== isFetching || $[28] !== isFetchingNextPage || $[29] !== isInitialLoading || $[30] !== onBottomIntersection || $[31] !== showEmptyState || $[32] !== waves) {
    t12 = <div className="tw-p-6">{isInitialLoading ? <div className="tw-flex tw-h-64 tw-items-center tw-justify-center"><div className="tw-flex tw-flex-col tw-items-center tw-gap-3"><CircleLoader size={CircleLoaderSize.LARGE} /><span className="tw-text-sm tw-text-iron-400">Loading waves...</span></div></div> : <>{error && <div className="tw-rounded-lg tw-border tw-border-rose-500/30 tw-bg-rose-500/10 tw-px-4 tw-py-3 tw-text-sm tw-text-rose-300">Unable to load waves right now. Please try again.</div>}{showEmptyState && <div className="tw-rounded-lg tw-border tw-border-iron-800/60 tw-bg-iron-900/40 tw-px-4 tw-py-3 tw-text-sm tw-text-iron-400">No waves created yet.</div>}{waves.length > 0 && <div className="tw-flex tw-flex-col tw-gap-3">{waves.map(_temp)}</div>}{(isFetchingNextPage || isFetching && waves.length > 0) && <div className="tw-mt-4 tw-flex tw-justify-center"><CircleLoader size={CircleLoaderSize.MEDIUM} /></div>}<CommonIntersectionElement onIntersection={onBottomIntersection} /></>}</div>;
    $[26] = error;
    $[27] = isFetching;
    $[28] = isFetchingNextPage;
    $[29] = isInitialLoading;
    $[30] = onBottomIntersection;
    $[31] = showEmptyState;
    $[32] = waves;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t11 || $[35] !== t12) {
    t13 = <div className={t11}>{t12}</div>;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t10 || $[38] !== t13) {
    t14 = <div className="tailwind-scope tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-white/5 tw-bg-[#0E1012] tw-shadow-[0_10px_40px_rgba(0,0,0,0.55)]">{t10}{t13}</div>;
    $[37] = t10;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  return t14;
};
function _temp(wave) {
  return <WaveCreatorPreviewItem key={wave.id} wave={wave} />;
}